import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Gift,
  SearchX,
  Send,
  Sparkles,
  Users,
} from 'lucide-react';
import { AccountSlots } from '../components/AccountSlots';
import { AppPicker, useApps } from '../components/AppPicker';
import { Drawer } from '../components/Drawer';
import { PageHeader } from '../components/Layout';
import { UserFilters } from '../components/UserFilters';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  Skeleton,
  Spinner,
  Textarea,
  cn,
} from '../components/ui';
import { api, compact } from '../lib/api';
import {
  formatDateTime,
  formatNumber,
  formatPlatform,
  formatRelative,
} from '../lib/format';
import { errorMessage, useToast } from '../lib/toast';
import type {
  AdminUser,
  AdminUserDetail,
  AppKey,
  Campaign,
  Page,
  UserFilter,
  UserSort,
  UserStatus,
} from '../lib/types';
import { readUsersParams, writeUsersParams } from '../lib/user-links';

const PAGE_SIZE = 25;

const SORT_OPTIONS: { value: UserSort; label: string; caption: string }[] = [
  { value: 'newest', label: 'Newest', caption: 'Showing the newest devices first' },
  { value: 'last_seen', label: 'Last seen', caption: 'Most recently seen first' },
  { value: 'sessions', label: 'Most sessions', caption: 'Most sessions first' },
];

export function UsersPage() {
  // The URL is the source of truth for app, filter and sort, so a dashboard
  // tile can deep-link here and a reload keeps the same list.
  const [params, setParams] = useSearchParams();
  const queryKey = params.toString();
  const { filter: urlFilter, sort } = useMemo(
    () => readUsersParams(new URLSearchParams(queryKey)),
    [queryKey],
  );
  const { app_key: appKey, ...filter } = urlFilter;

  /**
   * Cursor stack — the last entry is the current page, so "back" is a pop —
   * tagged with the query it pages through. A new filter or sort is a new
   * result set, and the stale stack is simply ignored rather than reset.
   */
  const [paging, setPaging] = useState<{
    key: string;
    cursors: (string | undefined)[];
  }>({ key: queryKey, cursors: [undefined] });
  const cursors = paging.key === queryKey ? paging.cursors : [undefined];
  const cursor = cursors[cursors.length - 1];
  const setCursors = (next: (string | undefined)[]) =>
    setPaging({ key: queryKey, cursors: next });

  const [selected, setSelected] = useState<string>();

  const apps = useApps();
  const query = useMemo<UserFilter>(() => compact(urlFilter), [urlFilter]);

  const users = useQuery({
    queryKey: ['users', query, sort, cursor],
    queryFn: () =>
      api.get<Page<AdminUser, string>>('/api/users', {
        ...query,
        sort: sort === 'newest' ? undefined : sort,
        limit: PAGE_SIZE,
        cursor,
      }),
    // Keeps the previous page on screen while the next one loads, so paging
    // doesn't flash an empty table.
    placeholderData: (previous) => previous,
  });

  /** Counting is a second scan, so it stays opt-in rather than automatic. */
  const [countRequested, setCountRequested] = useState(false);
  const total = useQuery({
    queryKey: ['users-count', query],
    queryFn: () => api.get<{ count: number }>('/api/users/count', query),
    enabled: countRequested,
  });

  const navigate = (next: UserFilter, nextSort: UserSort = sort) =>
    setParams(writeUsersParams(compact(next), nextSort), { replace: true });

  const changeFilter = (next: UserFilter) =>
    navigate({ ...next, app_key: appKey });

  // Switching app keeps the filter, except the package name, which belongs
  // to the app being left.
  const selectApp = (next: AppKey | undefined) =>
    navigate({ ...filter, app_package_name: undefined, app_key: next });

  return (
    <>
      <PageHeader
        title="Users"
        description="Browse registered devices, then grant a gift, a trial or a message"
      />

      <div className="space-y-4">
        <AppPicker
          value={appKey}
          onSelect={selectApp}
          onSelectAll={() => selectApp(undefined)}
        />

        <UserFilters
          value={filter}
          onChange={changeFilter}
          apps={apps.data?.find((app) => app.app_key === appKey)}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <p className="text-xs text-ink-2">
              {countRequested && total.data
                ? `${formatNumber(total.data.count)} devices match`
                : SORT_OPTIONS.find((option) => option.value === sort)?.caption}
            </p>
            {!countRequested && (
              <Button
                variant="ghost"
                className="text-xs"
                onClick={() => setCountRequested(true)}
              >
                Count matches
              </Button>
            )}
            {countRequested && total.isFetching && <Spinner className="size-4" />}
          </div>

          <label className="flex items-center gap-2 text-xs text-ink-2">
            Sort by
            <Select
              value={sort}
              onChange={(event) =>
                navigate(urlFilter, event.target.value as UserSort)
              }
              className="w-auto py-1.5 text-xs"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </label>
        </div>

        {users.isError && <ErrorNote error={users.error} />}

        <Card className="overflow-hidden">
          {users.isPending ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-12" />
              ))}
            </div>
          ) : users.data && users.data.items.length > 0 ? (
            <UserList
              users={users.data.items}
              showApp={!appKey}
              onSelect={(user) => setSelected(user.device_id)}
            />
          ) : (
            <EmptyState
              icon={<SearchX size={28} />}
              title="No devices match these filters"
              description="Try clearing a filter or widening the date range."
            />
          )}
        </Card>

        <Pagination
          canGoBack={cursors.length > 1}
          canGoForward={!!users.data?.has_more}
          loading={users.isFetching}
          onBack={() => setCursors(cursors.slice(0, -1))}
          onForward={() =>
            setCursors([...cursors, users.data?.next_cursor ?? undefined])
          }
        />
      </div>

      <UserDetailDrawer
        deviceId={selected}
        onClose={() => setSelected(undefined)}
        // Every device on that address, across all apps — the other filters
        // would only hide siblings.
        onFilterByIp={(ip) => {
          setSelected(undefined);
          navigate({ ip_address: ip });
        }}
      />
    </>
  );
}

// ── List ────────────────────────────────────────────────────────────────────

/**
 * A table from `md` up and a stack of cards below it — the same rows, laid out
 * for the pointer that is actually in use.
 */
function UserList({
  users,
  showApp,
  onSelect,
}: {
  users: AdminUser[];
  /** Across every app the row has to say which one it belongs to. */
  showApp: boolean;
  onSelect: (user: AdminUser) => void;
}) {
  return (
    <>
      <table className="hidden w-full text-sm md:table">
        <thead className="bg-raised text-xs text-ink-2">
          <tr>
            <th className="px-4 py-2.5 text-left font-medium">Device</th>
            <th className="px-4 py-2.5 text-left font-medium">Platform</th>
            <th className="px-4 py-2.5 text-left font-medium">Accounts</th>
            <th className="px-4 py-2.5 text-left font-medium">Status</th>
            <th className="px-4 py-2.5 text-left font-medium">Registered</th>
            <th className="px-4 py-2.5 text-left font-medium">Last seen</th>
            <th className="px-4 py-2.5 text-right font-medium">Sessions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {users.map((user) => (
            <tr
              key={user.id}
              onClick={() => onSelect(user)}
              className="cursor-pointer transition-colors hover:bg-raised"
            >
              <td className="max-w-[260px] px-4 py-2.5">
                <p className="truncate font-medium text-ink" title={user.device_id}>
                  {user.device_id}
                </p>
                <p className="truncate text-xs text-ink-3">
                  {user.device_model ?? 'Unknown model'}
                  {user.current_app_version ? ` · v${user.current_app_version}` : ''}
                  {user.language ? ` · ${user.language}` : ''}
                  {user.country ? ` · ${user.country}` : ''}
                </p>
              </td>
              <td className="px-4 py-2.5 text-ink-2">
                {formatPlatform(user.platform)}
                {showApp && (
                  <span className="block text-xs text-ink-3">{user.app_label}</span>
                )}
              </td>
              <td className="max-w-[200px] px-4 py-2.5">
                <AccountSlots accounts={user.accounts} variant="compact" />
              </td>
              <td className="px-4 py-2.5">
                <StatusBadges user={user} />
              </td>
              <td className="px-4 py-2.5 text-ink-2">
                {formatRelative(user.created_at)}
              </td>
              <td className="px-4 py-2.5 text-ink-2">
                {formatRelative(user.last_seen)}
              </td>
              <td className="tnum px-4 py-2.5 text-right text-ink-2">
                {formatNumber(user.session_count)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ul className="divide-y divide-line md:hidden">
        {users.map((user) => (
          <li key={user.id}>
            <button
              type="button"
              onClick={() => onSelect(user)}
              className="w-full px-4 py-3 text-left transition-colors hover:bg-raised"
            >
              <p className="truncate text-sm font-medium text-ink">
                {user.device_id}
              </p>
              <p className="mt-0.5 text-xs text-ink-3">
                {showApp ? `${user.app_label} · ` : ''}
                {formatPlatform(user.platform)}
                {user.device_model ? ` · ${user.device_model}` : ''}
                {user.current_app_version ? ` · v${user.current_app_version}` : ''}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <StatusBadges user={user} />
              </div>
              <div className="mt-1.5">
                <AccountSlots accounts={user.accounts} variant="compact" />
              </div>
              <p className="mt-2 text-xs text-ink-3">
                Registered {formatRelative(user.created_at)} · Seen{' '}
                {formatRelative(user.last_seen)} ·{' '}
                {formatNumber(user.session_count)} sessions
              </p>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function StatusBadges({ user }: { user: AdminUser }) {
  const badges = [
    user.is_premium && <Badge key="p" tone="good">Premium</Badge>,
    user.is_gift && (
      <Badge key="g" tone="brand" icon={<Gift size={11} />}>
        Gift
      </Badge>
    ),
    user.is_trial && (
      <Badge key="t" tone="warn" icon={<Sparkles size={11} />}>
        Trial
      </Badge>
    ),
    !user.is_active && <Badge key="i" tone="danger">Inactive</Badge>,
  ].filter(Boolean);

  if (badges.length === 0) return <Badge>Free</Badge>;
  return <span className="flex flex-wrap items-center gap-1.5">{badges}</span>;
}

function Pagination({
  canGoBack,
  canGoForward,
  loading,
  onBack,
  onForward,
}: {
  canGoBack: boolean;
  canGoForward: boolean;
  loading: boolean;
  onBack: () => void;
  onForward: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Button
        onClick={onBack}
        disabled={!canGoBack || loading}
        icon={<ChevronLeft size={16} />}
      >
        Previous
      </Button>
      {loading && <Spinner className="size-4" />}
      <Button onClick={onForward} disabled={!canGoForward || loading}>
        Next
        <ChevronRight size={16} />
      </Button>
    </div>
  );
}

// ── Detail & grants ─────────────────────────────────────────────────────────

/** The device drawer; also opened from the Shared IPs page. */
export function UserDetailDrawer({
  deviceId,
  onClose,
  onFilterByIp,
}: {
  deviceId: string | undefined;
  onClose: () => void;
  onFilterByIp: (ip: string) => void;
}) {
  const detail = useQuery({
    queryKey: ['user', deviceId],
    queryFn: () => api.get<AdminUserDetail>(`/api/users/${encodeURIComponent(deviceId!)}`),
    enabled: !!deviceId,
  });

  return (
    <Drawer
      open={!!deviceId}
      title={deviceId ?? ''}
      onClose={onClose}
      footer={deviceId && <GrantActions deviceId={deviceId} />}
    >
      {detail.isPending && <Spinner />}
      {detail.isError && <ErrorNote error={detail.error} />}
      {detail.data && (
        <div className="space-y-5">
          <UserDetail user={detail.data} onFilterByIp={onFilterByIp} />
          {/* Keyed so a half-written message never carries over to the next
              device the drawer opens on. */}
          <DirectMessage key={detail.data.device_id} user={detail.data} />
        </div>
      )}
    </Drawer>
  );
}

function UserDetail({
  user,
  onFilterByIp,
}: {
  user: AdminUserDetail;
  onFilterByIp: (ip: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-1.5">
        <StatusBadges user={user} />
        {user.has_fcm_token && <Badge tone="neutral">FCM token</Badge>}
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <DetailRow label="App" value={user.app_label} />
        <DetailRow label="Platform" value={formatPlatform(user.platform)} />
        <DetailRow label="Package" value={user.app_package_name} wide />
        <DetailRow label="Model" value={user.device_model} />
        <DetailRow label="OS version" value={user.device_software_version} />
        <DetailRow label="App version" value={user.current_app_version} />
        <DetailRow label="Language" value={user.language} />
        <DetailRow label="Country" value={user.country} />
        <DetailRow
          label="IP address"
          value={user.ip_address}
          href={user.ip_address && `${IP_LOOKUP}${encodeURIComponent(user.ip_address)}`}
          action={
            user.ip_address && (
              <button
                type="button"
                onClick={() => onFilterByIp(user.ip_address!)}
                title="List devices on this IP"
                aria-label="List devices on this IP"
                className="rounded p-0.5 text-ink-3 transition-colors hover:bg-raised hover:text-brand"
              >
                <Users size={14} />
              </button>
            )
          }
        />
        <DetailRow label="Plan" value={user.plan_id} wide />
        <DetailRow
          label="Subscription ends"
          value={formatDateTime(user.subscription_end_at)}
        />
        <DetailRow label="Trial ends" value={formatDateTime(user.trial_end_at)} />
        <DetailRow
          label="Gift ends"
          value={user.is_gift && !user.gift_end_at ? 'Never (lifetime)' : formatDateTime(user.gift_end_at)}
        />
        <DetailRow label="Registered" value={formatDateTime(user.created_at)} />
        <DetailRow label="Last seen" value={formatDateTime(user.last_seen)} />
        <DetailRow label="Sessions" value={user.session_count.toLocaleString()} />
      </dl>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          Accounts
        </h3>
        <AccountSlots accounts={user.accounts} />
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          Subscription history
        </h3>
        {user.history.length === 0 ? (
          <p className="text-sm text-ink-3">No store events recorded.</p>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {user.history.map((event) => (
              <li key={event.id} className="flex items-start justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink capitalize">
                    {event.type.replace(/_/g, ' ')}
                  </p>
                  <p className="truncate text-xs text-ink-3">
                    {event.plan_id ?? 'No plan id'}
                    {event.store ? ` · ${event.store}` : ''}
                    {event.environment === 'sandbox' ? ' · sandbox' : ''}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-ink-3">
                  {formatRelative(event.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Geo/ASN report for an IP — swap the host to change which service opens. */
export const IP_LOOKUP = 'https://ipinfo.io/';

function DetailRow({
  label,
  value,
  href,
  action,
  wide,
}: {
  label: string;
  value: string | null | undefined;
  /** When set alongside a value, the value opens this report in a new tab. */
  href?: string | null | false;
  /** A small control shown after the value. */
  action?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cn('min-w-0', wide && 'col-span-2')}>
      <dt className="text-xs text-ink-3">{label}</dt>
      <dd className="mt-0.5 flex items-center gap-1.5 break-anywhere text-ink">
        {value && href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            title={`Look up ${value}`}
            className="inline-flex items-center gap-1 text-brand hover:underline"
          >
            {value}
            <ExternalLink size={13} className="shrink-0 opacity-70" />
          </a>
        ) : (
          value || '—'
        )}
        {action}
      </dd>
    </div>
  );
}

/**
 * Gift and trial grants. Both push a notification server-side, so the device
 * finds out immediately rather than on its next launch.
 */
function GrantActions({ deviceId }: { deviceId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [giftDays, setGiftDays] = useState('');
  const [trialDays, setTrialDays] = useState('3');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['user', deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const grant = useMutation({
    mutationFn: (input: { path: string; body: Record<string, unknown> }) =>
      api.post<UserStatus>(input.path, input.body),
    onSuccess: (_data, input) => {
      toast.success(
        input.path.includes('revoke')
          ? 'Gift revoked'
          : 'Granted — a notification is on its way to the device',
      );
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const days = (value: string): number | undefined => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <Field label="Gift days" hint="Empty = lifetime" className="w-28">
          <Input
            type="number"
            min={1}
            value={giftDays}
            onChange={(event) => setGiftDays(event.target.value)}
            placeholder="∞"
          />
        </Field>
        <Button
          variant="primary"
          icon={<Gift size={16} />}
          loading={grant.isPending}
          onClick={() =>
            grant.mutate({
              path: '/api/gifts',
              body: { device_id: deviceId, duration_days: days(giftDays) },
            })
          }
        >
          Grant gift
        </Button>
        <Button
          variant="danger"
          loading={grant.isPending}
          onClick={() =>
            grant.mutate({
              path: '/api/gifts/revoke',
              body: { device_id: deviceId },
            })
          }
        >
          Revoke
        </Button>
      </div>

      <div className="flex items-end gap-2">
        <Field label="Trial days" className="w-28">
          <Input
            type="number"
            min={1}
            value={trialDays}
            onChange={(event) => setTrialDays(event.target.value)}
          />
        </Field>
        <Button
          icon={<Sparkles size={16} />}
          loading={grant.isPending}
          onClick={() =>
            grant.mutate({
              path: '/api/trials',
              body: { device_id: deviceId, duration_days: days(trialDays) },
            })
          }
        >
          Grant trial
        </Button>
      </div>
    </div>
  );
}


/**
 * A one-off push to this device, typed rather than templated. Sent as-is to
 * whatever language the device runs, stored in its in-app inbox, and logged
 * under Notifications → History.
 */
function DirectMessage({ user }: { user: AdminUserDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');

  const send = useMutation({
    mutationFn: () =>
      api.post<Campaign>('/api/notifications/direct', {
        device_id: user.device_id,
        message: compact({
          title: title.trim(),
          body: body.trim(),
          link: link.trim(),
        }),
      }),
    onSuccess: (campaign) => {
      if (campaign.sent_count > 0) {
        toast.success('Notification sent');
        setTitle('');
        setBody('');
        setLink('');
      } else if (campaign.skipped_count > 0) {
        toast.error(
          'Skipped — no usable push token, or the device or its build is silenced',
        );
      } else {
        toast.error('The push provider rejected the notification');
      }
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const ready = !!title.trim() && !!body.trim();

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
        Send a notification
      </h3>
      <div className="space-y-3 rounded-lg border border-line p-3">
        {!user.has_fcm_token && (
          <p className="text-xs text-warn">
            This device never reported an FCM token — Firebase-routed sends
            will skip it. It still lands in the in-app inbox.
          </p>
        )}
        <Field label="Title">
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={255}
            placeholder="Hi there 👋"
          />
        </Field>
        <Field label="Message">
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            maxLength={1000}
            rows={3}
            placeholder="What do you want to tell this user?"
          />
        </Field>
        <Field label="Link" hint="Optional — opened when the notification is tapped">
          <Input
            type="url"
            value={link}
            onChange={(event) => setLink(event.target.value)}
            maxLength={1024}
            placeholder="https://"
          />
        </Field>
        <div className="flex justify-end">
          <Button
            variant="primary"
            icon={<Send size={16} />}
            disabled={!ready}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            Send to this device
          </Button>
        </div>
      </div>
    </section>
  );
}
